import { cn, useErrorToast } from '@h2hspark/ui';
import { Bookmark, Check } from 'lucide-react';
import * as React from 'react';

import { useSetMeetListEntry } from './api';
import {
  MEET_LIST_ON_LIST_LABEL,
  MEET_LIST_SAVED_LABEL,
  MEET_LIST_SAVE_LABEL,
  MEET_LIST_SAVE_FAILED,
} from './constants';

/**
 * **Save to Meet, as one control wherever it is offered** (`MEM-013`, `MEM-025` AC-7).
 *
 * A match card and an attendee card both carry it, side by side on the same screen, and
 * `docs/standards/building-from-designs.md` is explicit that *"if a screen has two of anything …
 * they must use the same component"* — the failure it names is two tables on one screen with
 * different headers. This is that rule applied to the one control this screen draws nine times.
 *
 * **The mutation is here, and it is the only one.** `useSetMeetListEntry` writes `MEM-013`'s
 * per-event list; a card and a profile cannot drift into two lists because there is one write path.
 *
 * ## The two treatments the prototype draws
 *
 * `match` (§3.8) is a full-width pill in the card's button column: unsaved is the spark gradient in
 * white, saved is `--success` on `--success-bg` with a hairline. `attendee` (§3.9) is one of a
 * stacked pair at the right of a row: unsaved is the secondary outline, saved is the success
 * colours. They differ in **width and in the unsaved fill**, and in nothing else; both say the same
 * two words for the same state.
 *
 * `detail` (§2.16) is the left half of the pair at the foot of the attendee detail card — `flex:1`,
 * `--text-md`, 13px of padding, the secondary outline unsaved and the success colours saved. It is
 * the §3.9 treatment at the detail's larger type, which is what §2.16 draws: the same two words,
 * the same two states, a bigger button. Added 2026-09-12 with ruling 64.
 *
 * Renders nothing when the person did not resolve — there is no reference to save.
 */
export function SaveToMeetButton({
  eventId,
  attendeeRef,
  saved,
  variant,
}: {
  readonly eventId: string;
  readonly attendeeRef: string | null;
  readonly saved: boolean;
  readonly variant: 'match' | 'attendee' | 'detail';
}): React.ReactElement | null {
  const mutation = useSetMeetListEntry(eventId);
  const showError = useErrorToast();

  React.useEffect(() => {
    if (mutation.error !== null) showError(mutation.error, MEET_LIST_SAVE_FAILED);
  }, [mutation.error, showError]);

  if (attendeeRef === null) return null;

  return (
    <button
      type="button"
      disabled={mutation.isPending}
      onClick={() => {
        mutation.mutate({ attendeeRef, saved: !saved });
      }}
      className={cn(
        'inline-flex items-center justify-center gap-1.5 whitespace-nowrap rounded-pill font-brand font-bold disabled:opacity-60',
        /* **Full width in every treatment, so the label's length never moves anything** (Sumit,
           reviewing #529, ruling 28). The match card's column is the card; the attendee card's is
           `ACTION_COLUMN_CLASS`, a fixed width; the detail's is `flex-1` of a two-button row, so
           the pair splits the card in half whichever state each of them is in. What differs is the
           padding and the type size, not the footprint. */
        variant === 'match'
          ? 'w-full p-2.5 text-sm'
          : variant === 'attendee'
            ? 'w-full px-3 py-2.5 text-sm'
            : 'flex-1 basis-0 p-3 text-md',
        saved
          ? 'border-[1.5px] border-success bg-success-subtle text-success'
          : variant === 'match'
            ? 'border-[1.5px] border-transparent bg-spark text-white'
            : 'border-[1.5px] border-border bg-card text-text-body',
      )}
    >
      {saved ? (
        <Check className="h-4 w-4" aria-hidden />
      ) : (
        <Bookmark className="h-4 w-4" aria-hidden />
      )}
      {saved
        ? variant === 'match'
          ? MEET_LIST_SAVED_LABEL
          : MEET_LIST_ON_LIST_LABEL
        : MEET_LIST_SAVE_LABEL}
    </button>
  );
}
